import { X } from 'lucide-react';

import { useCallback, useRef, useState } from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import { Button } from '@splitfiesta/ui';

import { dragToFocalY, focalObjectPosition } from '@/utils/focalPoint';

interface PhotoFramerProps {
  src: string;
  /** Current focal point, 0=top … 1=bottom. */
  focalY: number;
  onChange: (focalY: number) => void;
  /** When provided, renders a remove button overlaid on the frame. */
  onRemove?: () => void;
}

/**
 * Drag-to-reposition control for a group cover photo (web).
 *
 * Renders the image covering a fixed 5:2 frame — matching the mobile hero crop —
 * and lets the user drag it vertically to choose which slice stays visible. The
 * cropping itself is pure CSS `object-cover` + `object-position`; the pointer
 * drag only adjusts the focal value via {@link dragToFocalY}.
 *
 * Dragging DOWN reveals the top (lower focalY); dragging UP reveals the bottom
 * (higher focalY). The overflow used to map pixels → focal delta is the cover
 * image's height (frame width ÷ natural ratio) minus the frame height, so the
 * drag tracks how far the hidden portion can actually scroll. When the image is
 * no taller than the frame (`overflow <= 0`) the drag is a no-op.
 */
export function PhotoFramer({
  src,
  focalY,
  onChange,
  onRemove,
}: PhotoFramerProps) {
  const { t } = useTranslation();
  const boxRef = useRef<HTMLDivElement>(null);
  const naturalRatioRef = useRef<number | null>(null);
  // Drag origin: pointer Y and the focal value when the drag started.
  const dragRef = useRef<{ startY: number; startFocalY: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const overflowPx = useCallback((): number => {
    const box = boxRef.current;
    const ratio = naturalRatioRef.current;
    if (!box || !ratio) return 0;
    const { width, height } = box.getBoundingClientRect();
    const imageHeight = width / ratio;
    return Math.max(0, imageHeight - height);
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (overflowPx() <= 0) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = { startY: e.clientY, startFocalY: focalY };
      setIsDragging(true);
    },
    [focalY, overflowPx]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dyPx = e.clientY - drag.startY;
      onChange(dragToFocalY(drag.startFocalY, dyPx, overflowPx()));
    },
    [onChange, overflowPx]
  );

  const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    dragRef.current = null;
    setIsDragging(false);
  }, []);

  return (
    <div
      ref={boxRef}
      data-testid="photo-framer"
      role="slider"
      aria-label="Drag to reposition the photo"
      aria-orientation="vertical"
      aria-valuemin={0}
      aria-valuemax={1}
      aria-valuenow={Math.round(focalY * 100) / 100}
      className={`relative aspect-[5/2] w-full overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-800 select-none touch-none ${
        isDragging ? 'cursor-grabbing' : 'cursor-ns-resize'
      }`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <img
        src={src}
        alt=""
        draggable={false}
        data-testid="photo-framer-image"
        className="absolute inset-0 h-full w-full object-cover"
        style={{ objectPosition: focalObjectPosition(focalY) }}
        onLoad={e => {
          const img = e.currentTarget;
          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            naturalRatioRef.current = img.naturalWidth / img.naturalHeight;
          }
        }}
      />
      {onRemove && (
        <Button
          variant="danger"
          size="sm"
          type="button"
          // Sits above the draggable image — swallow the pointer/click so it
          // removes the photo instead of starting a reposition drag.
          onPointerDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={t('common:actions.remove')}
          className="absolute top-2 right-2 z-10 rounded-full p-1 w-6 h-6 min-w-0"
          data-testid="photo-framer-remove"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
